import Clear from '@mui/icons-material/Clear';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Card, CardContent, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import {
  DataGrid,
  DataGridProps,
  GridApi,
  GridColDef,
  GridRowModes,
  GridRowModesModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { isEmpty, keys, last, reject, some, trim, values } from 'es-toolkit/compat';
import React from 'react';
import { useDebounce } from 'use-debounce';

import { DataGridLoadingOverlay } from 'components/atoms/BaseDataGrid/DataGridLoadingOverlay';
import { useSettingsDataGridStyles } from 'components/atoms/BaseDataGrid/Style';
import { autoGridRowHeight } from 'components/atoms/EditableDataGrid/helpers';
import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { getCheckboxColumn } from 'components/atoms/RowSelectionContext/checkboxColumn';
import { LoadingStage } from 'components/Loader';
import { UserCredentials } from 'interfaces/userCredentials';
import { UserCredentialsFieldsContext } from 'interfaces/userCredentials/UserCredentialsFieldsContext';
import { uuidv4 } from 'utils/helpers';
import {
  getPaginatedUserCredentialsQueryKey,
  usePaginatedUserCredentials,
} from 'utils/queryHooks/usePaginatedUserCredentials';
import { DEFAULT_SORT_FIELD, ROWS_PER_PAGE_OPTIONS } from './constants';
import { useUserCredentialsColumns } from './useUserCredentialsColumns';

const auth0FieldConversion: { [field: string]: string } = {
  emails: 'email',
};

const bulkEditMode = false;

interface UserDirectorySearchFieldProps {
  value: string;
  onChange: (nextValue: string) => void;
  compact?: boolean;
}

export const UserDirectorySearchField: React.FC<UserDirectorySearchFieldProps> = ({
  value,
  onChange,
  compact = false,
}) => {
  const trimmedSearch = trim(value);

  return (
    <TextField
      fullWidth
      size="small"
      type="search"
      label="Search users"
      placeholder="Name or email"
      value={value}
      helperText={
        compact
          ? undefined
          : trimmedSearch.length > 0 && trimmedSearch.length < 3
          ? 'Enter at least 3 characters to search.'
          : 'Search applies after 3 characters.'
      }
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchRoundedIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <IconButton
            onClick={() => {
              onChange('');
            }}
            size="small"
            aria-label="Clear user search"
          >
            <Clear />
          </IconButton>
        ) : undefined,
      }}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
};

interface UserCredentialsDataGridProps {
  manageNucleaiUsers: boolean;
  directorySearch: string;
  onDirectorySearchChange: (nextValue: string) => void;
}

export const UserCredentialsDataGrid: React.FC<React.PropsWithChildren<UserCredentialsDataGridProps>> = ({
  manageNucleaiUsers,
  directorySearch,
  onDirectorySearchChange,
}) => {
  const { isRowSelected } = useRowSelectionContext();
  const { fieldsContext } = useTableEditingContext<UserCredentials, UserCredentialsFieldsContext>();
  const gridApiRef = React.useRef<GridApi>(null);
  const { labId, isLoadingRoles, isLoadingPermissions } = fieldsContext;
  const [ascending, setAscending] = React.useState(true);
  const [sortField, setSortField] = React.useState(DEFAULT_SORT_FIELD);
  const [page, setPage] = React.useState(0);
  const [perPage, setPerPage] = React.useState(ROWS_PER_PAGE_OPTIONS[0]);

  const [debouncedLocalSearch] = useDebounce(directorySearch, 500);

  const isSearchRelevant = trim(debouncedLocalSearch).length >= 3;

  const transformedSearchQuery = isSearchRelevant ? trim(debouncedLocalSearch) : undefined;

  const paginatedUserCredentialsQueryKey = getPaginatedUserCredentialsQueryKey({
    labId,
    page,
    perPage,
    ascending,
    searchQuery: transformedSearchQuery,
    sortField: auth0FieldConversion[sortField] || sortField,
    listNucleaiUsers: Boolean(manageNucleaiUsers),
  });
  const { data: paginatedUsers, isLoading: isLoadingUserCredentials } = usePaginatedUserCredentials({
    labId,
    page,
    perPage,
    ascending,
    searchQuery: transformedSearchQuery,
    sortField: auth0FieldConversion[sortField] || sortField,
    listNucleaiUsers: Boolean(manageNucleaiUsers),
  });

  const pageUsers = paginatedUsers?.users || [];

  const [totalRows, setTotalRows] = React.useState(0);

  React.useEffect(() => {
    const newTotal = paginatedUsers?.total ?? totalRows;
    if (paginatedUsers && totalRows !== newTotal) {
      setTotalRows(newTotal);
    }
  }, [paginatedUsers, totalRows]);

  React.useEffect(() => {
    setPage(0);
  }, [directorySearch]);

  const isLoading = isLoadingUserCredentials || isLoadingRoles || isLoadingPermissions;

  const commonDataGridStyles = useSettingsDataGridStyles();

  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const isEditingRow = keys(rowModesModel).length > 0 && some(values(rowModesModel), { mode: GridRowModes.Edit });
  const handleRowModesModelChange: DataGridProps<UserCredentials>['onRowModesModelChange'] = React.useCallback(
    (newRowModesModel) => {
      // Support data grid events to start editing a new row, but not while another row is being edited.
      // Suppress switching the row to view without pressing the action buttons.
      if (reject(values(newRowModesModel), { mode: GridRowModes.Edit }).length == 1) {
        setRowModesModel(newRowModesModel);
      }
    },
    [setRowModesModel]
  );

  const userCredentialsColumns = useUserCredentialsColumns({
    apiRef: gridApiRef,
    manageNucleaiUsers,
    pageUsers,
    isEditingRow,
    noRows: isEmpty(pageUsers),
    bulkEditMode,
    rowModesModel,
    setRowModesModel,
    shouldApplyBulkChangesToRow: isRowSelected,
    paginatedUserCredentialsQueryKey,
  });

  const columns: GridColDef<UserCredentials>[] = [
    getCheckboxColumn(totalRows, bulkEditMode),
    ...userCredentialsColumns,
  ];

  return (
    <Grid container spacing={2} width="100%">
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Search Directory
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Filter by name or email. Search activates after 3 characters to keep the directory responsive.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <UserDirectorySearchField value={directorySearch} onChange={onDirectorySearchChange} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <DataGrid<UserCredentials>
          apiRef={gridApiRef}
          loading={isLoading}
          slots={{ loadingOverlay: DataGridLoadingOverlay }}
          slotProps={{ loadingOverlay: { stage: LoadingStage.LoadingUserCredentials } }}
          editMode="row"
          pagination
          autoHeight
          sortModel={[{ field: sortField, sort: ascending ? 'asc' : 'desc' }]}
          sortingMode="server"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onSortModelChange={(models: GridSortModel) => {
            const model = last(models);
            setSortField(model?.field || DEFAULT_SORT_FIELD);
            setAscending(!model?.sort || model?.sort === 'asc');
            setPage(0);
          }}
          columns={columns}
          // Stop editing a row when the user presses escape
          onCellKeyDown={({ id, field }, event) => {
            if (event.code === 'Escape' && rowModesModel[id]?.mode === GridRowModes.Edit) {
              gridApiRef.current?.stopCellEditMode({ id, field });
              gridApiRef.current?.stopRowEditMode({ id, field, ignoreModifications: true });
            }
          }}
          rows={pageUsers}
          getRowId={(row) => row?.id ?? uuidv4()}
          paginationModel={{ page, pageSize: perPage }}
          onPaginationModelChange={(newPaginationModel) => {
            setPage(newPaginationModel.page);
            setPerPage(newPaginationModel.pageSize);
          }}
          rowCount={totalRows}
          paginationMode="server"
          rowSelectionModel="none"
          pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
          onProcessRowUpdateError={console.error}
          hideFooterSelectedRowCount
          onCellClick={({ hasFocus }, event) => {
            if (!hasFocus && isEditingRow) {
              event.defaultMuiPrevented = true;
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onRowEditStop={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          sx={commonDataGridStyles}
          getRowHeight={autoGridRowHeight}
        />
      </Grid>
    </Grid>
  );
};
